import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useGastosContext } from '../../contexts/GastosContext'
import { useMetasContext } from '../../contexts/MetasContext'
import { useTheme } from '../../contexts/ThemeContext'
import { STORAGE_KEYS } from '../../lib/storage'
import { useInsights } from '../../hooks/useInsights'
import { useProfileContext } from '../../contexts/ProfileContext'
import ProgressRing from '../ui/ProgressRing'
import WeeklyResetBanner from '../ui/WeeklyResetBanner'
import EditTransaccionModal from '../ui/EditTransaccionModal'
import { CategoryIcon, IcTarget } from '../ui/Icons'

export default function Inicio() {
  const navigate = useNavigate()
  const { gastos, totales, loading: loadingG, editarGasto, eliminarGasto } = useGastosContext()
  const { metas, loading: loadingM, editarMeta } = useMetasContext()
  const { theme } = useTheme()
  const [txSeleccionada, setTxSeleccionada] = useState(null)
  const [surplusDismissed, setSurplusDismissed] = useState(() => {
    const key = STORAGE_KEYS.SURPLUS_DISMISSED(new Date().getFullYear(), new Date().getMonth())
    return !!localStorage.getItem(key)
  })
  const { profile } = useProfileContext()
  const insight = useInsights(gastos, metas)
  const s = makeStyles(theme)

  if (loadingG) return <div style={s.loading}>cargando tu mundo</div>

  const saldo = totales.ingresos - totales.gastos - totales.ahorros

  const hoy = new Date()
  const diasRestantes = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0).getDate() - hoy.getDate()
  const showSurplusBanner = !surplusDismissed && diasRestantes <= 5 && saldo > 0

  function dismissSurplus() {
    const key = STORAGE_KEYS.SURPLUS_DISMISSED(hoy.getFullYear(), hoy.getMonth())
    localStorage.setItem(key, '1')
    setSurplusDismissed(true)
  }

  async function handleSaveGasto(id, cambios) {
    const gasto = gastos.find(g => g.id === id)
    const result = await editarGasto(id, cambios)
    if (!result.error && gasto?.tipo === 'ahorro' && gasto?.meta_id && cambios.monto !== undefined) {
      const delta = cambios.monto - gasto.monto
      if (delta !== 0) {
        const meta = metas.find(m => m.id === gasto.meta_id)
        if (meta) await editarMeta(gasto.meta_id, { acumulado: Math.max(0, (meta.acumulado || 0) + delta) })
      }
    }
    return result
  }

  async function handleDeleteGasto(id) {
    const gasto = gastos.find(g => g.id === id)
    const result = await eliminarGasto(id)
    if (!result.error && gasto?.tipo === 'ahorro' && gasto?.meta_id) {
      const meta = metas.find(m => m.id === gasto.meta_id)
      if (meta) await editarMeta(gasto.meta_id, { acumulado: Math.max(0, (meta.acumulado || 0) - gasto.monto) })
    }
    return result
  }

  const metaPrincipal  = metas.find(m => m.status === 'en_progreso' || !m.status) || null
  const isGastandoMeta = metaPrincipal?.fase === 'gastando'
  const metaDisponible = isGastandoMeta
    ? Math.max(0, (metaPrincipal.acumulado || 0) - (metaPrincipal.gastado_meta || 0))
    : 0
  const metaPct = metaPrincipal && metaPrincipal.objetivo > 0
    ? Math.round((metaPrincipal.acumulado / metaPrincipal.objetivo) * 100)
    : 0
  const metaColor = metaPrincipal ? (theme[metaPrincipal.color] || theme.gold) : theme.gold

  const insightBg = insight.type === 'positive'
    ? theme.sagePale + '88'
    : insight.type === 'warning'
    ? theme.blush + 'CC'
    : theme.paper

  return (
    <div style={s.container}>
      {/* Banner semanal */}
      <WeeklyResetBanner gastos={gastos} metas={metas} />

      {/* Saludo */}
      <div style={s.greeting}>
        <p style={s.greetingSub}>{profile?.nombre ? `hola, ${profile.nombre}` : 'hola de nuevo'}</p>
        <h1 style={s.greetingTitle}>
          Tu dinero es tu <em style={{ color: theme.accent }}>libertad</em>
        </h1>
      </div>

      {/* Balance */}
      <div style={s.balanceCard}>
        <p style={s.balanceLabel}>disponible este mes</p>
        <h2 style={s.balanceAmount}>
          ${saldo.toLocaleString('es')}
        </h2>
        <p style={s.balanceSub}>
          {saldo >= 0 ? 'vas bien este mes' : 'atención a los gastos este mes'}
        </p>
        <div style={s.balanceRow}>
          <div>
            <p style={s.statLabel}>ingresos</p>
            <p style={{ ...s.statVal, color: theme.sage }}>${totales.ingresos.toLocaleString('es')}</p>
          </div>
          <div>
            <p style={s.statLabel}>gastos</p>
            <p style={{ ...s.statVal, color: 'rgba(255,255,255,0.9)' }}>${totales.gastos.toLocaleString('es')}</p>
          </div>
          <div>
            <p style={s.statLabel}>ahorrado</p>
            <p style={{ ...s.statVal, color: theme.goldPale }}>${totales.ahorros.toLocaleString('es')}</p>
          </div>
        </div>
      </div>

      {/* Insight del día */}
      <div style={{ ...s.insightCard, background: insightBg }}>
        <p style={s.insightText}>{insight.text}</p>
      </div>

      {/* Banner de sobrante fin de mes */}
      {showSurplusBanner && (
        <div style={s.surplusBanner}>
          <button style={s.surplusClose} onClick={dismissSurplus}>×</button>
          <p style={s.surplusTitle}>te quedan ${saldo.toLocaleString('es')} este mes</p>
          <p style={s.surplusSub}>{diasRestantes === 0 ? 'último día del mes' : `faltan ${diasRestantes} día${diasRestantes > 1 ? 's' : ''}`} — ¿qué haces con el sobrante?</p>
          <div style={s.surplusActions}>
            <button style={s.surplusBtn} onClick={() => navigate('/registro')}>registrar como ahorro ✦</button>
            <button style={s.surplusDismissBtn} onClick={dismissSurplus}>descartar</button>
          </div>
        </div>
      )}

      {/* Meta principal */}
      {!loadingM && (
        metaPrincipal ? (
          isGastandoMeta ? (
            <div
              style={{ ...s.metaStrip, background: theme.lav + '40', border: `1.5px solid ${theme.lav}55` }}
              onClick={() => navigate('/registro', { state: { gastandoMetaId: metaPrincipal.id } })}
            >
              <div style={s.metaStripPlaceholderIcon}>💸</div>
              <div style={s.metaStripInfo}>
                <p style={s.metaStripName}>{metaPrincipal.emoji} {metaPrincipal.nombre}</p>
                <p style={{ ...s.metaStripSub, color: theme.lav }}>
                  disponible para gastar · ${metaDisponible.toLocaleString('es')}
                </p>
              </div>
              <span style={{ ...s.metaStripArrow, color: theme.lav }}>›</span>
            </div>
          ) : (
            <div style={s.metaStrip} onClick={() => navigate('/metas')}>
              <ProgressRing size={56} pct={metaPct} color={metaColor} strokeWidth={6} gradientId="inicio-meta" />
              <div style={s.metaStripInfo}>
                <p style={s.metaStripName}>{metaPrincipal.emoji} {metaPrincipal.nombre}</p>
                <p style={s.metaStripSub}>
                  ${(metaPrincipal.acumulado || 0).toLocaleString('es')} de ${metaPrincipal.objetivo.toLocaleString('es')}
                </p>
              </div>
              <span style={{ ...s.metaStripArrow, color: metaColor }}>›</span>
            </div>
          )
        ) : (
          <div style={s.metaStrip} onClick={() => navigate('/metas')}>
            <div style={s.metaStripInfo}>
              <p style={s.metaStripName}>crea tu primera meta</p>
              <p style={s.metaStripSub}>dale dirección a tu dinero</p>
            </div>
            <span style={{ ...s.metaStripArrow, color: theme.gold }}>›</span>
          </div>
        )
      )}

      {/* Movimientos del mes */}
      {gastos.length > 0 && (
        <>
          <h3 style={s.sectionTitle}>Movimientos del mes</h3>
          {gastos.map(g => {
            const txIconBg = g.tipo === 'ingreso'
              ? theme.sage + '33'
              : g.tipo === 'ahorro'
              ? theme.gold + '2E'
              : g.emocion === 'Impulsivo'
              ? theme.mauve + '2E'
              : theme.txIconBg
            const txIconColor = g.tipo === 'ingreso'
              ? theme.sage
              : g.tipo === 'ahorro'
              ? theme.gold
              : g.emocion === 'Impulsivo'
              ? theme.mauve
              : theme.terraDeep
            return (
              <div key={g.id} style={s.txItem} onClick={() => setTxSeleccionada(g)}>
                <div style={{ ...s.txIcon, background: txIconBg, color: txIconColor }}>
                  {g.tipo === 'ahorro'
                    ? <IcTarget size={20} />
                    : <CategoryIcon category={g.categoria} size={20} />
                  }
                </div>
                <div style={s.txInfo}>
                  <p style={s.txName}>{g.descripcion}</p>
                  <p style={s.txMeta}>
                    {new Date(g.fecha).toLocaleDateString('es', { day: 'numeric', month: 'short' })} · {g.categoria}{g.emocion ? ` · ${g.emocion}` : ''}
                  </p>
                </div>
                <p style={{ ...s.txAmount, color: g.tipo === 'ingreso' ? theme.sage : g.tipo === 'ahorro' ? theme.gold : theme.terra }}>
                  {g.tipo === 'ingreso' ? '+' : g.tipo === 'ahorro' ? '' : '-'}${g.monto.toLocaleString('es')}
                </p>
              </div>
            )
          })}
          <p style={s.txFooter}>toca un registro para editar ✦</p>
        </>
      )}

      {gastos.length === 0 && (
        <div style={s.emptyTx}>
          <p style={s.emptyTxText}>sin movimientos este mes. empieza cuando quieras.</p>
        </div>
      )}

      {txSeleccionada && (
        <EditTransaccionModal
          gasto={txSeleccionada}
          onSave={handleSaveGasto}
          onDelete={handleDeleteGasto}
          onClose={() => setTxSeleccionada(null)}
        />
      )}
    </div>
  )
}

const makeStyles = (t) => ({
  container: {
    maxWidth: 560, margin: '0 auto',
    padding: '80px 28px 120px',
    fontFamily: "'DM Sans', sans-serif",
    background: t.cream, minHeight: '100vh',
  },
  loading: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    height: '100vh', fontFamily: "'DM Sans', sans-serif",
    fontSize: 15, color: t.inkLight,
  },
  greeting: { marginBottom: 24 },
  greetingSub: { fontFamily: "'Lora', serif", fontStyle: 'italic', fontSize: 17, color: t.inkLight, marginBottom: 4 },
  greetingTitle: { fontFamily: "'Cormorant Garamond',serif", fontSize: 36, fontWeight: 300, color: t.ink },
  balanceCard: {
    background: t.cardGradient, borderRadius: 24, padding: '28px 26px', color: 'white',
    marginBottom: 16, boxShadow: `0 16px 40px ${t.accentDeep}44`,
  },
  balanceLabel: { fontSize: 12, letterSpacing: 2, textTransform: 'uppercase', opacity: 0.6, marginBottom: 6 },
  balanceAmount: { fontFamily: "'Cormorant Garamond',serif", fontSize: 56, fontWeight: 600, lineHeight: 1 },
  balanceSub: { fontFamily: "'Lora', serif", fontStyle: 'italic', fontSize: 15, opacity: 0.75, marginTop: 6 },
  balanceRow: {
    display: 'flex', gap: 24, marginTop: 18, paddingTop: 18,
    borderTop: '1px solid rgba(255,255,255,0.2)',
  },
  statLabel: { fontSize: 12, opacity: 0.6, textTransform: 'uppercase', letterSpacing: 1 },
  statVal: { fontFamily: "'Cormorant Garamond',serif", fontSize: 22, fontWeight: 600 },
  metaStrip: {
    display: 'flex', alignItems: 'center', gap: 14,
    background: t.goldPale + '60', borderRadius: 18,
    padding: '14px 16px', marginBottom: 14, cursor: 'pointer',
    border: `1.5px solid ${t.gold}33`,
  },
  metaStripInfo: { flex: 1, minWidth: 0 },
  metaStripName: {
    fontFamily: "'Cormorant Garamond',serif", fontSize: 16, fontWeight: 500,
    color: t.ink, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  metaStripSub: { fontFamily: "'Lora', serif", fontStyle: 'italic', fontSize: 13, color: t.inkLight, margin: 0 },
  metaStripPlaceholderIcon: { fontSize: 28, flexShrink: 0 },
  metaStripArrow: { fontSize: 22, fontWeight: 300, flexShrink: 0 },
  insightCard: {
    borderRadius: 16, padding: '14px 18px', marginBottom: 24,
    border: `1px solid ${t.sage}33`,
  },
  insightText: {
    fontFamily: "'Lora', serif", fontStyle: 'italic', fontSize: 15, color: t.inkMid, margin: 0, lineHeight: 1.6,
  },
  sectionTitle: {
    fontFamily: "'Cormorant Garamond',serif", fontSize: 22, fontWeight: 400,
    color: t.ink, marginBottom: 12,
  },
  txItem: {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '13px 0', borderBottom: `1px solid ${t.txBorder}`,
    cursor: 'pointer',
  },
  txFooter: {
    fontFamily: "'Lora', serif", fontStyle: 'italic', fontSize: 13, color: t.inkLight,
    textAlign: 'center', paddingTop: 14,
  },
  txIcon: {
    width: 42, height: 42, borderRadius: 14, background: t.txIconBg,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0, color: t.terraDeep,
  },
  txInfo: { flex: 1, minWidth: 0 },
  txName: { fontSize: 15, fontWeight: 500, color: t.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  txMeta: { fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: t.inkLight, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  surplusBanner: {
    position: 'relative', background: t.goldPale + '88', border: `1.5px solid ${t.gold}55`,
    borderRadius: 18, padding: '16px 18px', marginBottom: 16,
  },
  surplusClose: {
    position: 'absolute', top: 10, right: 14, background: 'none', border: 'none',
    fontSize: 20, color: t.inkLight, cursor: 'pointer', lineHeight: 1,
  },
  surplusTitle: {
    fontFamily: "'Cormorant Garamond', serif", fontSize: 20, fontWeight: 600,
    color: t.ink, marginBottom: 4, paddingRight: 24,
  },
  surplusSub: {
    fontFamily: "'Lora', serif", fontStyle: 'italic', fontSize: 14, color: t.inkLight, marginBottom: 14,
  },
  surplusActions: { display: 'flex', gap: 10 },
  surplusBtn: {
    flex: 1, padding: '11px 14px', borderRadius: 14, border: 'none',
    background: t.cardGradient, color: 'white',
    fontFamily: "'Cormorant Garamond', serif", fontSize: 16, fontStyle: 'italic',
    cursor: 'pointer',
  },
  surplusDismissBtn: {
    padding: '11px 14px', borderRadius: 14, border: `1.5px solid ${t.gold}55`,
    background: 'transparent', fontFamily: "'DM Sans', sans-serif",
    fontSize: 13, color: t.inkLight, cursor: 'pointer',
  },
  txAmount: { fontFamily: "'Cormorant Garamond',serif", fontSize: 18, fontWeight: 600 },
  emptyTx: { textAlign: 'center', padding: '32px 0' },
  emptyTxText: { fontFamily: "'Lora', serif", fontStyle: 'italic', fontSize: 15, color: t.inkLight },
})
